/**
 * Visual Viewport Bridge — Wave 4
 *
 * Tracks `window.visualViewport` and publishes its live geometry to the
 * document root as CSS custom properties. Enables sheets/HUDs/menus to
 * lock themselves to Safari's *actually visible* window, immune to the
 * collapsing address bar, the keyboard, and orientation flips.
 *
 *   --visual-viewport-height  px (innerHeight fallback)
 *   --visual-viewport-width   px
 *   --visual-viewport-top     px  (offsetTop, for translateY shift)
 *   --visual-viewport-left    px  (offsetLeft)
 *
 * Also publishes a coarse landscape lock marker so a single CSS rule can
 * suppress the page's background bounce while a mobile HUD is mounted.
 *
 *   html[data-orientation="landscape" | "portrait"]
 *   html[data-coarse-pointer="true"]
 *
 * rAF-batched. Idempotent. Returns a teardown function.
 */
export function installVisualViewportBridge(): () => void {
  if (typeof window === "undefined") return () => {};

  const root = document.documentElement;
  let frame = 0;

  const write = () => {
    frame = 0;
    const vv = window.visualViewport;
    const height = vv ? vv.height : window.innerHeight;
    const width = vv ? vv.width : window.innerWidth;
    const top = vv ? vv.offsetTop : 0;
    const left = vv ? vv.offsetLeft : 0;

    root.style.setProperty("--visual-viewport-height", `${height}px`);
    root.style.setProperty("--visual-viewport-width", `${width}px`);
    root.style.setProperty("--visual-viewport-top", `${top}px`);
    root.style.setProperty("--visual-viewport-left", `${left}px`);

    const landscape = width >= height;
    root.dataset.orientation = landscape ? "landscape" : "portrait";
  };

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(write);
  };

  // Coarse-pointer marker — published once and refreshed on media-query flips.
  const coarse = window.matchMedia("(pointer: coarse)");
  const writeCoarse = () => {
    root.dataset.coarsePointer = coarse.matches ? "true" : "false";
  };
  writeCoarse();
  coarse.addEventListener?.("change", writeCoarse);

  write();

  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener("resize", schedule);
    vv.addEventListener("scroll", schedule);
  }
  window.addEventListener("resize", schedule, { passive: true });
  window.addEventListener("orientationchange", schedule, { passive: true });

  return () => {
    if (frame) cancelAnimationFrame(frame);
    coarse.removeEventListener?.("change", writeCoarse);
    if (vv) {
      vv.removeEventListener("resize", schedule);
      vv.removeEventListener("scroll", schedule);
    }
    window.removeEventListener("resize", schedule);
    window.removeEventListener("orientationchange", schedule);
  };
}
